import { createSelector } from 'reselect'
import { getCheckoutLoyaltyClassicRewards } from 'checkout/lib/utils/classicRewards/classicRewardCheckoutState'
import getItemViewAppliedLoyaltyUpgrade from 'checkout/lib/utils/loyalty/getItemViewAppliedLoyaltyUpgrade'
import getAccommodationV2ItemViews from 'checkout/selectors/view/itemViews/getAccommodationV2ItemViews'
import getBedbankItemViews from 'checkout/selectors/view/itemViews/getBedbankItemViews'
import getHotelItemViews from 'checkout/selectors/view/itemViews/getHotelItemViews'
import getLoyaltyInsuranceBenefitItemViews from 'checkout/selectors/view/itemViews/getLoyaltyInsuranceBenefitItemViews'
import getLoyaltyLoungePassItemViews from 'checkout/selectors/view/itemViews/getLoyaltyLoungePassItemViews'
import {
  AGENT_HUB_PAYMENT_MODE,
  CLASSIC_REWARD_PAYMENT_TYPE,
  LOYALTY_DISCOUNTED_INSURANCE,
  PROMO_PAYMENT_TYPE,
  VOUCHER_PAYMENT_TYPE,
} from 'constants/payment'
import { nonNullable } from 'lib/array/arrayUtils'
import { isExperienceItem, isTransferItem } from 'lib/checkout/checkoutUtils'
import { isVoucherAppliedAsCheckoutDiscount } from 'lib/voucher/isVoucherAppliedAsCheckoutDiscount'
import { shouldSkipGhaDiscountPreviewVoucher } from 'lib/voucher/shouldSkipGhaDiscountPreviewVoucher'

// Mirrors the voucher payments CheckoutPurchaseBox adds when the order is placed
function hasVoucherPayment(promotion: App.Promotion | undefined): boolean {
  return !!promotion?.items.some(item => (item.appliedVoucher ?? []).some(voucher =>
    !shouldSkipGhaDiscountPreviewVoucher(item, voucher) &&
    (!isVoucherAppliedAsCheckoutDiscount(voucher) || voucher.amount > 0),
  ))
}

function hasLoyaltyTransferPayment(cartItems: Array<App.Checkout.AnyItem>): boolean {
  return cartItems.some(item =>
    (isExperienceItem(item) && item.tickets.some(ticket => ticket.loyaltyTransferDiscount)) ||
    (isTransferItem(item) && item.isLoyaltyTransfer && item.hasLoyaltyBenefit),
  )
}

// Mirrors the Classic Reward payment CheckoutPurchaseBox adds for each reward flight in the cart
function hasClassicRewardPayment(
  classicRewards: Array<App.Checkout.ClassicRewardCheckoutState>,
  cartItems: Array<App.Checkout.AnyItem>,
): boolean {
  return classicRewards.some(reward => cartItems.some(item => item.transactionKey === reward.itemTransactionKey))
}

/**
 * The promo, voucher, Société benefit and Classic Reward payments the order will be placed with. svc-lux-loyalty
 * classifies the order from every payment it records, so the quote has to include these too.
 */
const getCheckoutLoyaltyEarnDiscountPaymentTypes = createSelector(
  (state: App.State) => state.checkout.promo,
  (state: App.State) => state.checkout.commission,
  (state: App.State) => state.checkout.payment.agentHubPaymentMode,
  (state: App.State) => getLoyaltyInsuranceBenefitItemViews(state),
  (state: App.State) => getLoyaltyLoungePassItemViews(state).data,
  (state: App.State) => getHotelItemViews(state).data,
  (state: App.State) => getBedbankItemViews(state).data,
  (state: App.State) => getAccommodationV2ItemViews(state).data,
  (state: App.State) => state.checkout.cart.items,
  (state: App.State) => getCheckoutLoyaltyClassicRewards(state),
  (
    promotion,
    commission,
    agentHubPaymentMode,
    insuranceBenefitViews,
    loungePassViews,
    hotelOfferViews,
    bedbankOfferViews,
    accommodationV2OfferViews,
    cartItems,
    classicRewards,
  ): Array<string> => {
    const accommodationItemViews = [
      ...hotelOfferViews.flatMap(offerView => offerView.itemViews),
      ...bedbankOfferViews.flatMap(offerView => offerView.itemViews),
      ...accommodationV2OfferViews.flatMap(offerView => offerView.itemViews),
    ]
    // An Agent Hub booking paid at the net amount records the agent's commission as a promo payment
    const hasCommissionPayment = !!commission && agentHubPaymentMode === AGENT_HUB_PAYMENT_MODE.PAY_NET_AMOUNT

    return nonNullable([
      (promotion?.code || hasCommissionPayment) ? PROMO_PAYMENT_TYPE : undefined,
      hasVoucherPayment(promotion) ? VOUCHER_PAYMENT_TYPE : undefined,
      insuranceBenefitViews.length ? LOYALTY_DISCOUNTED_INSURANCE : undefined,
      loungePassViews.some(view => view.item.isFree) ? 'le_points_lounge_pass' : undefined,
      accommodationItemViews.some(itemView => getItemViewAppliedLoyaltyUpgrade(itemView)) ? 'le_points_hotel_upgrade' : undefined,
      hasLoyaltyTransferPayment(cartItems) ? 'le_points_airport_transfer' : undefined,
      hasClassicRewardPayment(classicRewards, cartItems) ? CLASSIC_REWARD_PAYMENT_TYPE : undefined,
    ])
  },
)

export default getCheckoutLoyaltyEarnDiscountPaymentTypes
